import { Place, Trip } from "./interfaces";
import { isLodging } from "./placeFunctions";
import { computeDaySlots, computeLodgingBars } from "../components/TripCalendar/tripCalendarLogic";
import { TSDate } from "./time";
import { isTripOver, tripDateRange, tripStatus } from "./tripStatus";

export type ProblemType = 'missing-lodging' | 'draft-item';

export interface TripProblem {
    type: ProblemType;
    description: string;
    trip: Trip;
    day?: TSDate;
    place?: Place;
}

export function findProblemsForTrip(trip: Trip, allTripPlaces: Place[]): TripProblem[] {
    const problems: TripProblem[] = [];

    // A trip that's over has nothing left to plan: an unfinished draft or a gap in lodging is
    // history, not a to-do. Flagging it just trains you to ignore the warnings that matter.
    if (isTripOver(tripStatus(trip, allTripPlaces))) {
        return problems;
    }

    // 1. Detect Drafts
    allTripPlaces.forEach(place => {
        if (place.isdraft) {
            problems.push({
                type: 'draft-item',
                description: `Draft: ${place.title ?? 'Untitled'}`,
                trip,
                place
            });
        }
    });

    // 2. Reuse established range and calendar lodging logic. tripDateRange filters out the legacy
    //    1900 "no date" sentinel — without that guard the day-by-day walk below spans ~46,000
    //    iterations per trip, on every render of the home screen.
    const range = tripDateRange(trip, allTripPlaces);
    if (!range) return problems;

    const { start: tripStart, end: tripEnd } = range;
    const lodgings = allTripPlaces.filter(p => isLodging(p) && p.startdatetime && p.enddatetime);
    const slots = computeDaySlots(lodgings);

    // Build the full range array
    const week: TSDate[] = [];
    let d = tripStart;
    while (d.isBefore(tripEnd) || d.equals(tripEnd)) {
        week.push(d);
        d = d.plus({ days: 1 });
    }

    const lodgingBars = computeLodgingBars(week, slots, tripStart, tripEnd);
    // Check bars for warnings
    const reportedDays = new Set<number>();
    const today = TSDate.today();
    lodgingBars.forEach(bar => {
        if (bar.warn) {
            for (let i = bar.colStart - 1; i < bar.colEnd - 1; i++) {
                const dayIndex = Math.floor(i / 2);
                if (dayIndex >= 0 && dayIndex < week.length) {
                    const day = week[dayIndex];
                    if (day.isAfter(today) && !reportedDays.has(day.daysSinceEpoch)) {
                        problems.push({
                            type: 'missing-lodging',
                            description: `Missing lodging for ${day.toLocalDateTime().toLocaleString({ month: 'short', day: 'numeric' })}`,
                            trip,
                            day: day
                        });
                        reportedDays.add(day.daysSinceEpoch);
                    }
                }
            }
        }
    });

    return problems;
}
